import React, { useState, useEffect, useRef, useCallback } from 'react';
import 'rsuite/dist/rsuite.min.css';
import '../../../scenes/QueueView.scss';
import '../filterModal.scss';
import { useGetLoginUserDataQuery } from 'redux/apiSlice';
import useApiManager from 'networking/ApiManager';
import { Pagination, CoreInputBox, Loader } from 'whealth-core-web/components';
import { ToastContainer, toast } from 'react-toastify';
import { Button } from 'rsuite';
import { filters, closeIcon } from 'res/images';

const RefundPayment = () => {
  const ApiClient = useApiManager();
  let userData = useGetLoginUserDataQuery()?.data?.data || {};
  const value = useRef('');

  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [countData, setCountData] = useState(0);
  const [availableBalance, setAvailableBalance] = useState(0);
  const [startCount, setStartCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hideEmpty, setHideEmpty] = useState(true);
  const [refundingId, setRefundingId] = useState(null);

  // Filters
  const [searchStr, setSearchStr] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [appliedFromDate, setAppliedFromDate] = useState('');
  const [appliedToDate, setAppliedToDate] = useState('');

  const fetchData = useCallback(() => {
    setIsLoading(true);
    let params = { page: currentPage };
    if (searchStr.trim()) params.search = searchStr.trim();
    if (appliedFromDate) params.from_date = appliedFromDate;
    if (appliedToDate) params.to_date = appliedToDate;

    ApiClient.getCancelledUnrefunded(params)
      .then((res) => {
        setIsLoading(false);
        setHideEmpty(false);
        const results = res.data?.results || [];
        setData(results);
        setCountData(res.data?.count || 0);
        setAvailableBalance(res.data?.razorpay_balance?.balance || 0);
        const pageSize = 10;
        setTotalPages(Math.ceil((res.data?.count || 0) / pageSize) || 1);
        let startPage = pageSize * currentPage - pageSize;
        setStartCount(startPage);
      })
      .catch((err) => {
        setIsLoading(false);
        setHideEmpty(false);
        setData([]);
        console.error('Error fetching refund data:', err);
      });
  }, [currentPage, searchStr, appliedFromDate, appliedToDate]);

  useEffect(() => {
    if (userData?.id) {
      fetchData();
    }
  }, [userData, currentPage, appliedFromDate, appliedToDate]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchStr.length > 2 || searchStr.length === 0) {
        setCurrentPage(1);
        fetchData();
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [searchStr]);

  const handleRefund = (appointmentId) => {
    const confirmRefund = window.confirm('Are you sure you want to refund this appointment?');
    if (!confirmRefund) return;

    setRefundingId(appointmentId);
    ApiClient.refundAppointment(appointmentId)
      .then((res) => {
        setRefundingId(null);
        toast.success('Refund initiated successfully');
        fetchData();
      })
      .catch((err) => {
        setRefundingId(null);
        toast.error(err?.response?.data?.detail || err?.response?.data?.message || 'Refund failed. Please try again.');
      });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  const renderHeader = () => {
    return (
      <>
        <div className='col-md-4'>
          <CoreInputBox
            placeholder='Search by patient name or phone number'
            setValue={(val) => {
              setCurrentPage(1);
              value.current = val;
              setSearchStr(val);
            }}
            value={searchStr}
            inputStyle={{ height: '24px', fontSize: '9px' }}
          />
        </div>
        <div className='col-md-3'>
          Available Balance : ₹ {availableBalance}
        </div>

        <div className='col-md-1' style={{ float: 'right' }}>
          <div
            data-bs-toggle='offcanvas'
            data-bs-target='#offcanvasRight'
            aria-controls='offcanvasRight'
          >
            <Button
              className='m-0 reusableBtn reusableBtnActive'
              style={{ fontSize: '9px' }}
            >
              <img src={filters} style={{ height: '12px', marginRight: '4px' }} />
              {'Filter'}
            </Button>
          </div>
        </div>
      </>
    );
  };

  const renderFilterChips = () => {
    if (!appliedFromDate && !appliedToDate) return null;
    return (
      <div className='d-flex align-items-center gap-2 mt-2' style={{ paddingLeft: '12px', flexWrap: 'wrap' }}>
        {appliedFromDate && (
          <span
            style={{
              background: '#e9ecef', borderRadius: '16px', padding: '4px 12px',
              fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px',
            }}
          >
            From: {appliedFromDate}
            <span
              style={{ cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' }}
              onClick={() => { setAppliedFromDate(''); setFromDate(''); setCurrentPage(1); }}
            >×</span>
          </span>
        )}
        {appliedToDate && (
          <span
            style={{
              background: '#e9ecef', borderRadius: '16px', padding: '4px 12px',
              fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px',
            }}
          >
            To: {appliedToDate}
            <span
              style={{ cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' }}
              onClick={() => { setAppliedToDate(''); setToDate(''); setCurrentPage(1); }}
            >×</span>
          </span>
        )}
        <span
          style={{ cursor: 'pointer', color: '#dc3545', fontSize: '12px', fontWeight: 500 }}
          onClick={() => {
            setAppliedFromDate(''); setAppliedToDate('');
            setFromDate(''); setToDate('');
            setCurrentPage(1);
          }}
        >
          Clear All
        </span>
      </div>
    );
  };

  // Side drawer (offcanvas) for date filters
  const renderFilterDrawer = () => (
    <div className='filterModal'>
      <div className='offcanvas offcanvas-end' tabIndex='-1' id='offcanvasRight' aria-labelledby='offcanvasRightLabel'>
        <div className='whiteBackground'>
          <div className='filterHeader'>
            <div>Apply Filter</div>
            <div data-bs-dismiss='offcanvas' aria-label='Close' style={{ cursor: 'pointer' }}>
              <img src={closeIcon} />
            </div>
          </div>
          <div className='hrLine' />
          <div className='filterHeaderData'>
            <div className='mt-3'>
              <div className='filterHeadings'>Date Range</div>
              <div className='mt-3'>
                <label style={{ fontSize: '13px', fontWeight: 500, color: '#555', marginBottom: '4px', display: 'block' }}>
                  From Date
                </label>
                <input
                  type='date'
                  className='form-control'
                  style={{ fontSize: '13px' }}
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>
              <div className='mt-3'>
                <label style={{ fontSize: '13px', fontWeight: 500, color: '#555', marginBottom: '4px', display: 'block' }}>
                  To Date
                </label>
                <input
                  type='date'
                  className='form-control'
                  style={{ fontSize: '13px' }}
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>
            </div>
          </div>
          <div className='renderFooter'>
            <div className='hrLine' />
            <div className='footerButtons'>
              <div
                className='button clearBtn'
                data-bs-dismiss='offcanvas'
                aria-label='Close'
                onClick={() => {
                  setFromDate('');
                  setToDate('');
                  setAppliedFromDate('');
                  setAppliedToDate('');
                  setCurrentPage(1);
                }}
              >
                Clear All
              </div>
              <div
                className='button applyBtn'
                data-bs-dismiss='offcanvas'
                aria-label='Close'
                onClick={() => {
                  setAppliedFromDate(fromDate);
                  setAppliedToDate(toDate);
                  setCurrentPage(1);
                }}
              >
                Apply Filter
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const showDetails = () => (
    <>
      <div className='teamHeader stickyHeader'>
        <div className='headerFilesRow'>{renderHeader()}</div>
        <div className='hrLine' />
        {renderFilterChips()}
        <div className='row'>
          <div className='col-md-12'>
            <table className='table table-bordered table-hover'>
              <thead>
                <tr className='table-theme'>
                  <th className='table-theme'>Sno</th>
                  <th className='table-theme'>Patient Name</th>
                  <th className='table-theme'>Phone</th>
                  <th className='table-theme'>Doctor</th>
                  <th className='table-theme'>Location</th>
                  <th className='table-theme'>Appointment Date</th>
                  <th className='table-theme'>Fee (₹)</th>
                  <th className='table-theme'>Payment Status</th>
                  <th className='table-theme'>Payment Mode</th>
                  <th className='table-theme'>Action</th>
                </tr>
              </thead>
              <tbody>
                {data.length === 0 && !isLoading ? (
                  <tr>
                    <td colSpan='10' className='text-center' style={{ padding: '40px', color: '#888' }}>
                      No cancelled unrefunded appointments found.
                    </td>
                  </tr>
                ) : (
                  data.map((item, index) => (
                    <tr key={item.id}>
                      <td className='align-content-center'>{startCount + index + 1}</td>
                      <td className='align-content-center'>{item.user?.full_name || '-'}</td>
                      <td className='align-content-center'>{item.user?.phone || '-'}</td>
                      <td className='align-content-center'>{item.consultant?.full_name || '-'}</td>
                      <td className='align-content-center'>{item.location?.name || '-'}</td>
                      <td className='align-content-center'>
                        {formatDate(item.consult_datetime)}
                        <br />
                        <small style={{ color: '#888' }}>{formatTime(item.consult_datetime)}</small>
                      </td>
                      <td className='align-content-center'>₹{item.appointment_fee || '0'}</td>
                      <td className='align-content-center'>
                        <span
                          style={{
                            padding: '3px 10px', borderRadius: '12px', fontSize: '11px',
                            fontWeight: 500, textTransform: 'capitalize',
                            background: item.payment?.payment_status === 'paid' ? '#d4edda' : '#f8d7da',
                            color: item.payment?.payment_status === 'paid' ? '#155724' : '#721c24',
                          }}
                        >
                          {item.payment?.payment_status || '-'}
                        </span>
                      </td>
                      <td className='align-content-center' style={{ textTransform: 'capitalize' }}>
                        {item.payment?.payment_mode || '-'}
                      </td>
                      <td className='align-content-center'>
                        {!userData?.read_only && (
                        <Button
                          className='m-0'
                          disabled={refundingId === item.id}
                          onClick={() => handleRefund(item.id)}
                          style={{
                            background: refundingId === item.id ? '#b7b7b7' : '#dc3545',
                            color: '#fff', border: 'none', borderRadius: '6px',
                            padding: '4px 14px', fontSize: '11px', fontWeight: 500,
                            cursor: refundingId === item.id ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {refundingId === item.id ? 'Processing...' : 'Refund'}
                        </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className='usersList wrapperContent'>
        <div className='d-flex justify-content-end mb-4'>
          <Pagination
            data={data}
            length={countData}
            totalPages={totalPages}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            page='refund'
          />
        </div>
      </div>
    </>
  );

  return (
    <div className='wrapper'>
      {showDetails()}
      {renderFilterDrawer()}
      <Loader show={isLoading} />
      <ToastContainer />
    </div>
  );
};

export default RefundPayment;
